import React, { useState, useEffect } from 'react';
import Box from '../Common/Box';
import { pushService } from '../../services/pushService';
import { isStandalone, isIOSDevice } from '../../hooks/usePWAInstall';
import './Pwa.css';

type PushState = 'loading' | 'unsupported' | 'server-disabled' | 'ios-not-installed' | 'off' | 'on' | 'denied';

/**
 * Carte « Notifications push » de la page Mes Alertes : activation/désactivation
 * des alertes marché sur l'appareil courant.
 */
const PushNotificationSettings: React.FC = () => {
  const [state, setState] = useState<PushState>('loading');
  const [busy, setBusy] = useState(false);
  const [feedback, setFeedback] = useState<string>('');

  useEffect(() => {
    let cancelled = false;

    const init = async () => {
      if (!pushService.isSupported()) {
        // iOS : l'API Push n'existe qu'une fois l'app installée sur l'écran d'accueil.
        setState(isIOSDevice() && !isStandalone() ? 'ios-not-installed' : 'unsupported');
        return;
      }
      if (typeof Notification !== 'undefined' && Notification.permission === 'denied') {
        setState('denied');
        return;
      }
      try {
        const config = await pushService.getServerConfig();
        if (cancelled) return;
        if (!config.enabled) {
          setState('server-disabled');
          return;
        }
        const subscribed = await pushService.isSubscribed();
        if (!cancelled) setState(subscribed ? 'on' : 'off');
      } catch {
        if (!cancelled) setState('server-disabled');
      }
    };

    init();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleToggle = async () => {
    setBusy(true);
    setFeedback('');
    try {
      if (state === 'on') {
        await pushService.unsubscribe();
        setState('off');
        setFeedback('Notifications désactivées sur cet appareil.');
      } else {
        await pushService.subscribe();
        setState('on');
        try {
          await pushService.sendTest();
          setFeedback('Activé ! Une notification de test vient de vous être envoyée.');
        } catch {
          setFeedback('Notifications activées sur cet appareil.');
        }
      }
    } catch (err) {
      if (typeof Notification !== 'undefined' && Notification.permission === 'denied') {
        setState('denied');
      } else {
        setFeedback(err instanceof Error ? err.message : "Une erreur est survenue.");
      }
    } finally {
      setBusy(false);
    }
  };

  if (state === 'loading' || state === 'unsupported' || state === 'server-disabled') {
    // Rien à afficher : navigateur incompatible ou push non configuré côté serveur.
    return null;
  }

  return (
    <Box className='push-settings-card'>
      <div className='push-settings-row'>
        <div className='push-settings-icon'>
          <i className='material-icons'>notifications_active</i>
        </div>
        <div className='push-settings-text'>
          <strong>Notifications push</strong>
          {state === 'ios-not-installed' ? (
            <span>
              Sur iPhone/iPad, installez d'abord l'application (Partager → « Sur l'écran
              d'accueil ») pour recevoir les alertes.
            </span>
          ) : state === 'denied' ? (
            <span>
              Notifications bloquées par le navigateur. Autorisez-les dans les paramètres du
              site pour recevoir vos alertes.
            </span>
          ) : (
            <span>Recevez vos alertes prix et signaux directement sur cet appareil.</span>
          )}
          {feedback && <span className='push-settings-feedback'>{feedback}</span>}
        </div>
        {(state === 'on' || state === 'off') && (
          <button
            type='button'
            role='switch'
            aria-checked={state === 'on'}
            aria-label='Activer les notifications push'
            className={`push-toggle ${state === 'on' ? 'on' : ''}`}
            onClick={handleToggle}
            disabled={busy}
          >
            <span className='push-toggle-thumb' />
          </button>
        )}
      </div>
    </Box>
  );
};

export default PushNotificationSettings;
